import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Combobox } from "@/components/Combobox";
import { toast } from "sonner";
import { HandCoins, Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { AcertoTotais } from "./acerto/AcertoTotais";
import { AcertoTable } from "./acerto/AcertoTable";
import { RegistrarAcertoDialog } from "./acerto/RegistrarAcertoDialog";
import { AcertosHistorico } from "./acerto/AcertosHistorico";
import { AcertosHistoricoFiltros } from "./acerto/AcertosHistoricoFiltros";
import { calcTotais, fmtD, periodoPreset } from "./acerto/acertoHelpers";

const PRESETS = [["hoje", "Hoje"], ["ontem", "Ontem"], ["7d", "7 dias"], ["mes", "Este mês"]];
const HIST_VAZIO = { entregador_id: "", start: "", end: "" };

/**
 * Acerto com Entregador (Iter 116) — fecha o caixa do entregador: soma o troco que saiu,
 * o dinheiro recebido e o que ele deve devolver; registra o acerto e marca as entregas.
 * Permissões (Iter 117): recurso `acertos` — V ver/exportar · I registrar · A editar · E desfazer.
 */
export default function Acerto() {
  const { user, currentEmpresaId } = useAuth();
  const canRegister = hasPerm(user, "acertos", "i");
  const canEdit = hasPerm(user, "acertos", "a");
  const canUndo = hasPerm(user, "acertos", "e");
  const [entregadores, setEntregadores] = useState([]);
  const [pendentes, setPendentes] = useState([]);
  const [entregadorId, setEntregadorId] = useState("");
  const [periodo, setPeriodo] = useState(periodoPreset("hoje"));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histFiltros, setHistFiltros] = useState(HIST_VAZIO);

  const loadBase = useCallback(async () => {
    const [e, p] = await Promise.all([api.get("/entregadores"), api.get("/acertos/pendentes").catch(() => ({ data: { itens: [] } }))]);
    setEntregadores(e.data || []);
    setPendentes(p.data?.itens || []);
  }, []);
  const loadHistorico = useCallback(async () => {
    if (histFiltros.start && histFiltros.end && histFiltros.start > histFiltros.end) return;
    setHistLoading(true);
    try {
      const params = { limit: 200, ...Object.fromEntries(Object.entries(histFiltros).filter(([, v]) => v)) };
      const r = await api.get("/acertos", { params }); setHistorico(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao carregar histórico"); }
    finally { setHistLoading(false); }
  }, [histFiltros]);
  useEffect(() => { loadBase(); }, [currentEmpresaId, loadBase]);
  useEffect(() => { loadHistorico(); }, [currentEmpresaId, loadHistorico]);

  const loadPreview = useCallback(async () => {
    if (!entregadorId || !periodo.start || !periodo.end) { setPreview(null); return; }
    setLoading(true);
    try {
      const r = await api.get("/acertos/preview", { params: { entregador_id: entregadorId, start: periodo.start, end: periodo.end } });
      setPreview(r.data);
      setSelected(new Set((r.data.entregas || []).map((x) => x.id)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao carregar entregas"); setPreview(null); }
    finally { setLoading(false); }
  }, [entregadorId, periodo.start, periodo.end]);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const entregasSel = useMemo(() => (preview?.entregas || []).filter((e) => selected.has(e.id)), [preview, selected]);
  const totais = useMemo(() => calcTotais(entregasSel), [entregasSel]);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => (s.size === (preview?.entregas || []).length ? new Set() : new Set(preview.entregas.map((x) => x.id))));

  const abrirPendente = (p) => { setEntregadorId(p.entregador_id); setPeriodo({ start: p.mais_antiga, end: periodoPreset("hoje").end }); };
  const afterRegistro = () => { setConfirmOpen(false); loadPreview(); loadBase(); loadHistorico(); };
  const invalido = periodo.start && periodo.end && periodo.start > periodo.end;

  return (
    <div className="space-y-4" data-testid="page-acerto">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><HandCoins className="w-7 h-7 text-orange-600" />Acerto com Entregador</h1>
          <p className="text-sm text-muted-foreground">Escolha o entregador e o período: o sistema soma o troco que saiu e o dinheiro que ele deve devolver. Confirme e as entregas ficam marcadas como acertadas.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { loadBase(); loadPreview(); loadHistorico(); }} data-testid="acerto-recarregar"><RefreshCw className="w-4 h-4 mr-1" />Atualizar</Button>
      </div>

      {pendentes.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-3" data-testid="acerto-pendentes">
          <div className="text-xs font-bold uppercase text-amber-900 mb-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Entregadores com acerto pendente — toque para abrir</div>
          <div className="flex flex-wrap gap-2">
            {pendentes.map((p) => (
              <button key={p.entregador_id} type="button" onClick={() => abrirPendente(p)} data-testid={`acerto-pendente-${p.entregador_id}`}
                className={`rounded-xl border px-3 py-2 text-left bg-white hover:border-orange-400 hover:shadow transition-all ${p.entregador_id === entregadorId ? "border-orange-500 ring-2 ring-orange-200" : "border-amber-200"}`}>
                <div className="text-sm font-bold">{p.nome}</div>
                <div className="text-[11px] text-slate-600">{p.qtd} entrega(s) · desde {fmtD(p.mais_antiga)}</div>
                <div className="text-xs mono"><span className="text-orange-700">troco {fmtBRL(p.troco_saiu)}</span> · <span className="text-emerald-800 font-semibold">receber {fmtBRL(p.a_receber)}</span></div>
              </button>
            ))}
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div className="col-span-2">
            <Label className="text-xs">Entregador</Label>
            <Combobox options={entregadores} value={entregadorId} onChange={(v) => setEntregadorId(v || "")} placeholder="Selecione o entregador" testId="acerto-entregador" allowClear={false} />
          </div>
          <div>
            <Label className="text-xs">De</Label>
            <Input type="date" value={periodo.start} onChange={(e) => setPeriodo({ ...periodo, start: e.target.value })} data-testid="acerto-start" />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input type="date" value={periodo.end} onChange={(e) => setPeriodo({ ...periodo, end: e.target.value })} data-testid="acerto-end" />
          </div>
          <div className="col-span-2 flex gap-1 flex-wrap">
            {PRESETS.map(([k, l]) => {
              const pr = periodoPreset(k); const on = pr.start === periodo.start && pr.end === periodo.end;
              return <Button key={k} size="sm" variant={on ? "default" : "outline"} className={`h-8 text-xs ${on ? "bg-orange-600 hover:bg-orange-700" : ""}`} onClick={() => setPeriodo(pr)} data-testid={`acerto-preset-${k}`}>{l}</Button>;
            })}
          </div>
          {invalido && <div className="col-span-2 md:col-span-6 text-xs text-rose-700" data-testid="acerto-periodo-invalido">Data inicial maior que a final.</div>}
        </CardContent>
      </Card>

      {!entregadorId && <div className="text-sm text-muted-foreground text-center py-6" data-testid="acerto-selecione">Selecione um entregador (ou toque em um card de pendência) para começar.</div>}

      {entregadorId && (
        <div className="space-y-3">
          {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Carregando entregas…</div>}
          {preview && (
            <>
              <AcertoTotais totais={totais} selecionadas={entregasSel.length} totalEntregas={preview.entregas.length} />
              {preview.ja_acertadas > 0 && <div className="text-xs text-slate-500" data-testid="acerto-ja-acertadas">{preview.ja_acertadas} entrega(s) deste período já constam em acertos anteriores (não listadas).</div>}
              <AcertoTable entregas={preview.entregas} selected={selected} toggle={toggle} toggleAll={toggleAll} />
              <div className="flex items-center justify-end gap-3 sticky bottom-2">
                <div className="rounded-2xl border bg-white/95 backdrop-blur px-4 py-2 flex items-center gap-4 shadow-lg">
                  <div className="text-sm"><span className="text-muted-foreground">Troco que saiu </span><b className="mono text-orange-700" data-testid="acerto-rodape-troco">{fmtBRL(totais.troco_saiu)}</b></div>
                  <div className="text-sm"><span className="text-muted-foreground">A receber </span><b className="mono text-emerald-800 text-base" data-testid="acerto-rodape-receber">{fmtBRL(totais.a_receber)}</b></div>
                  <Button onClick={() => setConfirmOpen(true)} disabled={!canRegister || entregasSel.length === 0} className="bg-emerald-700 hover:bg-emerald-800 font-bold h-10" data-testid="acerto-registrar"
                    title={!canRegister ? "Sem permissão para registrar acerto" : undefined}>
                    <HandCoins className="w-4 h-4 mr-2" />Registrar acerto ({entregasSel.length})
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div>
        <h2 className="text-base font-bold mb-2">Histórico de acertos</h2>
        <AcertosHistoricoFiltros entregadores={entregadores} filtros={histFiltros} setFiltros={setHistFiltros} total={historico.length} />
        <AcertosHistorico acertos={historico} loading={histLoading} canEdit={canEdit} canUndo={canUndo} onChanged={afterRegistro} />
      </div>

      <RegistrarAcertoDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} entregador={preview?.entregador} periodo={periodo} totais={totais}
        entregaIds={entregasSel.map((e) => e.id)} onDone={afterRegistro} />
    </div>
  );
}
